// src/components/VideoWall.js
// Wall-mounted "video wall" / F&B menu-board: three screens joined into one
// unit, each running its OWN offering's photo slideshow. A power button toggles
// the wall off, and three "fault" buttons take the screens over with animated
// problem states (slow / blue screen / can't start) — a playful nod to the
// "Servis Baiki Komputer" offering.
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Power, Gauge, AlertTriangle, Ban, Wrench, CheckCircle2, Sparkles, LayoutGrid } from 'lucide-react';

const MAX_PROBE = 20;
const EXTS = ['jpeg', 'jpg', 'png', 'webp'];
const AUTO_MS = 3200;

// mode: 'normal' | 'off' | 'slow' | 'bluescreen' | 'noboot'

function useFolderImages(folder, base) {
  const [imgs, setImgs] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const probeStem = (stem) =>
      new Promise((resolve) => {
        let idx = 0;
        const tryNext = () => {
          if (idx >= EXTS.length) return resolve(null);
          const src = `${folder}/${stem}.${EXTS[idx++]}`;
          const im = new Image();
          im.onload = () => resolve(src);
          im.onerror = tryNext;
          im.src = src;
        };
        tryNext();
      });
    const stems = [base, ...Array.from({ length: MAX_PROBE }, (_, n) => `${base}${n + 1}`)];
    Promise.all(stems.map(probeStem)).then((res) => {
      if (!cancelled) setImgs(res.filter(Boolean));
    });
    return () => {
      cancelled = true;
    };
  }, [folder, base]);
  return imgs;
}

/* ---------- Fault overlays shown on every screen ---------- */
const FaultBlueScreen = () => (
  <div className="vw-flicker absolute inset-0 z-30 flex flex-col justify-center bg-[#0a5bd3] px-2 sm:px-3 py-4 text-white">
    <div className="font-mono text-2xl sm:text-3xl leading-none">:(</div>
    <p className="mt-2 font-mono text-[9px] sm:text-[11px] leading-snug">
      PC anda menghadapi masalah dan perlu dimulakan semula.
    </p>
    <p className="mt-2 font-mono text-[8px] sm:text-[10px] text-white/80">
      Mengumpul maklumat ralat… <span className="vw-blink">100%</span>
    </p>
    <p className="mt-2 font-mono text-[8px] sm:text-[9px] text-white/70">
      STOP CODE: PSS_SLOW_TERLALU_LAMA
    </p>
  </div>
);

const FaultNoBoot = () => (
  <div className="absolute inset-0 z-30 overflow-hidden bg-black">
    {/* sweeping glitch bar */}
    <div className="vw-glitch absolute inset-x-0 h-6 bg-white/15" />
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 text-center">
      <span className="font-mono text-[10px] sm:text-xs text-[#ff5555]">NO BOOT DEVICE</span>
      <span className="vw-blink font-mono text-[9px] sm:text-[11px] text-white/80">
        Operating System not found_
      </span>
      <span className="mt-1 font-mono text-[8px] text-white/40">Tekan sebarang kekunci…</span>
    </div>
  </div>
);

const FaultOff = () => (
  <div className="absolute inset-0 z-30 grid place-items-center bg-[#050505]">
    <span className="font-mono text-[10px] tracking-widest text-white/25">NO SIGNAL</span>
  </div>
);

const BusyRing = () => (
  <div className="absolute inset-0 z-30 grid place-items-center bg-black/45">
    <div className="vw-spin h-8 w-8 rounded-full border-[3px] border-white/25 border-t-white/90" />
  </div>
);

// Repair-in-progress: progress bar sweeping to 100% over the dark screen.
const SolvingOverlay = () => (
  <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-[#0d1117] px-3 text-center">
    <span className="font-mono text-[10px] sm:text-xs text-[#8dff8d]">ZABRA · MEMBAIKI…</span>
    <div className="h-2 w-3/4 overflow-hidden rounded-full bg-white/15">
      <div className="vw-progress h-full rounded-full bg-[#8dff8d]" />
    </div>
    <span className="font-mono text-[8px] text-white/50">Mengoptimum sistem</span>
  </div>
);

// Fixed: green "OK" state, calm and resolved.
const SolvedOverlay = () => (
  <div className="vw-fadein absolute inset-0 z-30 flex flex-col items-center justify-center gap-1 bg-gradient-to-b from-[#0f4d24] to-[#0a3319] px-3 text-center text-white">
    <CheckCircle2 className="h-9 w-9 text-[#8dff8d]" />
    <span className="font-bold text-xs sm:text-sm">Selesai!</span>
    <span className="font-mono text-[9px] text-white/70">Sistem kembali normal ✓</span>
  </div>
);

// "We solve EVERYTHING" — a ticking checklist of problem types, all cleared.
// Ties back to Zabra = SOLUTIONS.
const SOLUTION_ITEMS = [
  'Komputer perlahan',
  'Blue screen',
  'Tak boleh start',
  'Virus & malware',
  'Data hilang',
  'Rangkaian & printer',
];
const SolutionsOverlay = () => (
  <div className="absolute inset-0 z-30 flex flex-col justify-center gap-1.5 bg-gradient-to-br from-[#101826] to-[#0a1f12] px-3 py-3 text-white">
    <div className="mb-1 flex items-center gap-1.5">
      <Sparkles className="h-4 w-4 text-[var(--z-accent)]" />
      <span className="font-bold text-[11px] sm:text-xs">Zabra menyelesaikan semua:</span>
    </div>
    {SOLUTION_ITEMS.map((label, k) => (
      <div
        key={label}
        className="vw-checkitem flex items-center gap-1.5"
        style={{ animationDelay: `${k * 0.18}s` }}
      >
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[#8dff8d]" />
        <span className="font-mono text-[9px] sm:text-[10px] text-white/85">{label}</span>
      </div>
    ))}
  </div>
);

/* ---------- One screen in the wall ---------- */
const WallScreen = ({ folder, base, index, mode, offset = 0 }) => {
  const imgs = useFolderImages(folder, base);
  const [i, setI] = useState(0);
  const timer = useRef(null);

  // Start each screen on a different image so the 3 don't show the same frame.
  useEffect(() => {
    if (imgs.length) setI(offset % imgs.length);
  }, [imgs.length, offset]);

  // slideshow only runs in normal mode
  useEffect(() => {
    if (mode !== 'normal' || imgs.length <= 1) return;
    const start = setTimeout(() => {
      timer.current = setInterval(() => setI((p) => (p + 1) % imgs.length), AUTO_MS);
    }, index * 700);
    return () => {
      clearTimeout(start);
      clearInterval(timer.current);
    };
  }, [imgs.length, index, mode]);

  const showImage = mode === 'normal' || mode === 'slow';

  return (
    <div className="relative flex-1 min-w-0">
      <div className="relative overflow-hidden bg-black aspect-[3/4] sm:aspect-[4/3]">
        {/* panel scanlines + vignette */}
        <div className="pointer-events-none absolute inset-0 z-10 opacity-[0.10] [background-image:repeating-linear-gradient(0deg,#000_0,#000_1px,transparent_1px,transparent_3px)]" />
        <div className="pointer-events-none absolute inset-0 z-10 shadow-[inset_0_0_60px_rgba(0,0,0,0.55)]" />

        {/* base image */}
        {showImage && imgs.length > 0 && (
          <AnimatePresence mode="wait">
            <motion.img
              key={imgs[i]}
              src={imgs[i]}
              alt={`Servis & konsultasi komputer ${i + 1}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className={`absolute inset-0 h-full w-full object-cover ${mode === 'slow' ? 'vw-slow' : ''}`}
            />
          </AnimatePresence>
        )}

        {/* fault + repair overlays */}
        {mode === 'slow' && <BusyRing />}
        {mode === 'bluescreen' && <FaultBlueScreen />}
        {mode === 'noboot' && <FaultNoBoot />}
        {mode === 'off' && <FaultOff />}
        {mode === 'solving' && <SolvingOverlay />}
        {mode === 'solved' && <SolvedOverlay />}
        {mode === 'solutions' && <SolutionsOverlay />}

        {/* channel tag */}
        <div className="absolute top-2 left-2 z-40 rounded bg-[var(--z-accent)] px-1.5 py-0.5 font-mono text-[10px] font-bold text-white">
          0{index + 1}
        </div>
      </div>
    </div>
  );
};

/* ---------- The wall + controls ---------- */
const PROBLEMS = [
  { key: 'slow', label: 'Slow', icon: Gauge },
  { key: 'bluescreen', label: 'Blue Screen', icon: AlertTriangle },
  { key: 'noboot', label: 'Tak Boleh Start', icon: Ban },
];

const FAULTS = ['slow', 'bluescreen', 'noboot'];
const norm = () => ['normal', 'normal', 'normal'];
const mixed = () => ['slow', 'bluescreen', 'noboot']; // 01 slow · 02 blue · 03 no-boot

const VideoWall = ({ offerings, dark = false }) => {
  // All 3 screens show the "Servis & Konsultasi Komputer" photos, alternating.
  const servis =
    offerings.find((o) => /servis|baiki/i.test(o.folder || '') || /servis|baiki/i.test(o.title || '')) ||
    offerings[offerings.length - 1];
  // Each screen has its OWN mode so they can show different faults at once.
  // Default: "Semua Serentak" — each screen starts with a different fault.
  const [modes, setModes] = useState(mixed);
  const [powered, setPowered] = useState(true);
  const timers = useRef([]);

  const primary = modes[0]; // representative state for captions
  const anyFault = modes.some((m) => FAULTS.includes(m));
  const allHealthy = modes.every((m) => m === 'normal'); // no fault, not mid-repair

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  const setAll = (m) => setModes([m, m, m]);

  // Power only toggles the display on/off — it PRESERVES the current faults.
  // Problems clear only via "Zabra Selesaikan!" or the problem buttons.
  const togglePower = () => setPowered((p) => !p);

  // Single problem on ALL screens (press again to clear).
  const pickProblem = (key) => {
    clearTimers();
    setModes((m) => (m[0] === key && m[1] === key && m[2] === key ? norm() : [key, key, key]));
  };

  // Each screen its OWN problem: 01 slow · 02 bluescreen · 03 noboot.
  const mixedProblems = () => {
    clearTimers();
    setModes((m) =>
      m[0] === 'slow' && m[1] === 'bluescreen' && m[2] === 'noboot' ? norm() : mixed()
    );
  };

  // Repair the current fault(s): solving → solved → normal.
  const solveIt = () => {
    clearTimers();
    setAll('solving');
    timers.current.push(setTimeout(() => setAll('solved'), 1800));
    timers.current.push(setTimeout(() => setAll('normal'), 3600));
  };

  // "We also solve EVERYTHING else" (Zabra = Solutions): a showcase of the full
  // list of problems we handle. Only offered when screens are healthy — it's a
  // reveal, not a repair. Plays the checklist montage, then returns to normal.
  const showAllSolutions = () => {
    clearTimers();
    setAll('solutions');
    timers.current.push(setTimeout(() => setAll('normal'), 3800));
  };

  useEffect(() => clearTimers, []);

  const on = powered;

  return (
    <div className="mx-auto max-w-5xl">
      {/* Wall-mounted unit (no stand) — brushed white-metallic frame */}
      <div
        className={`metal-frame rounded-xl p-1.5 ${on ? 'vw-poweron' : ''}`}
        key={on ? 'on' : 'off'} /* re-trigger power-on flash */
      >
        <div className="flex flex-row overflow-hidden rounded-lg border-2 border-[var(--z-ink)] divide-x-2 divide-[var(--z-ink)]">
          {[0, 1, 2].map((index) => (
            <WallScreen
              key={index}
              folder={servis.folder}
              base={servis.base}
              index={index}
              offset={index * 2 + 1} /* alternate: each screen starts on a different photo */
              mode={on ? modes[index] : 'off'}
            />
          ))}
        </div>
      </div>

      {/* ===== Controls (the "remote" for the wall) ===== */}
      {/* Row 1: Power */}
      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={togglePower}
          aria-label="Kuasa paparan"
          className={`inline-flex items-center gap-2 rounded-full border-2 border-[var(--z-ink)] px-4 py-2 text-sm font-bold shadow-[0_4px_0_0_var(--z-ink)] active:translate-y-0.5 transition ${
            on ? 'bg-[var(--z-accent)] text-white' : 'bg-white text-[var(--z-ink)]'
          }`}
        >
          <Power size={16} /> {on ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Row 2: Grouped "Masalah" picker — label shows once */}
      <div className={`mx-auto mt-4 max-w-3xl rounded-2xl border-2 px-4 py-3 ${dark ? 'border-white/15 bg-white/[0.05]' : 'border-[var(--z-line)] bg-white'}`}>
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <span className={`inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider ${dark ? 'text-white/60' : 'text-[var(--z-muted)]'}`}>
            <AlertTriangle size={14} className="text-[var(--z-accent)]" /> Masalah:
          </span>

          {PROBLEMS.map(({ key, label, icon: Icon }) => {
            const active = modes.every((m) => m === key);
            return (
              <button
                key={key}
                type="button"
                disabled={!on}
                onClick={() => pickProblem(key)}
                className={`inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--z-ink)] px-3.5 py-2 text-sm font-bold shadow-[0_3px_0_0_var(--z-ink)] active:translate-y-0.5 transition disabled:opacity-40 disabled:cursor-not-allowed ${
                  active ? 'bg-[var(--z-ink)] text-white' : 'bg-white text-[var(--z-ink)] hover:bg-[var(--z-accent-50)]'
                }`}
              >
                <Icon size={15} /> {label}
              </button>
            );
          })}

          {/* different fault on each screen at once */}
          <button
            type="button"
            disabled={!on}
            onClick={mixedProblems}
            className={`inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--z-ink)] px-3.5 py-2 text-sm font-bold shadow-[0_3px_0_0_var(--z-ink)] active:translate-y-0.5 transition disabled:opacity-40 disabled:cursor-not-allowed ${
              modes[0] === 'slow' && modes[1] === 'bluescreen' && modes[2] === 'noboot'
                ? 'bg-[var(--z-ink)] text-white'
                : 'bg-white text-[var(--z-ink)] hover:bg-[var(--z-accent-50)]'
            }`}
          >
            <LayoutGrid size={15} /> Semua Serentak
          </button>
        </div>
      </div>

      {/* Row 3: one action at a time — fix while broken, showcase once healthy */}
      <div className="mt-5 flex min-h-[3.25rem] flex-wrap items-center justify-center gap-3">
        <AnimatePresence mode="wait">
          {/* While broken: only the repair button (green = solution light) */}
          {on && anyFault && (
            <motion.div
              key="fix"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <button type="button" onClick={solveIt} className="btn-solve text-base">
                <Wrench size={18} /> Zabra Selesaikan!
              </button>
            </motion.div>
          )}

          {/* Once healthy: reveal the full list of what else Zabra solves */}
          {on && allHealthy && (
            <motion.div
              key="more"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <button type="button" onClick={showAllSolutions} className="btn-outline text-base">
                <Sparkles size={18} /> Apa Lagi Kami Selesaikan?
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* helper caption / workshop notes */}
      <p className={`mt-4 text-center text-sm max-w-xl mx-auto ${dark ? 'text-white/70' : 'text-[var(--z-muted)]'}`}>
        {!on && 'Paparan dimatikan — tekan butang kuasa untuk hidupkan semula.'}
        {on && modes[0] === 'slow' && modes[1] === 'bluescreen' && modes[2] === 'noboot' &&
          'Setiap skrin, masalah berbeza! Tekan "Zabra Selesaikan!" — kami baiki kesemuanya sekali gus.'}
        {on && allHealthy && 'PC sihat ✓ Cuba butang masalah di atas, atau lihat "Apa Lagi Kami Selesaikan?". 🔧'}
        {on && primary === 'slow' && modes[1] === 'slow' && 'Komputer perlahan & tersekat-sekat? Tekan "Zabra Selesaikan!".'}
        {on && primary === 'bluescreen' && modes[1] === 'bluescreen' && 'Blue screen of death! Tekan "Zabra Selesaikan!" — kami pulihkan sistem anda.'}
        {on && primary === 'noboot' && modes[1] === 'noboot' && 'Komputer tak boleh start langsung? Tekan "Zabra Selesaikan!".'}
        {on && primary === 'solving' && '🔧 Pasukan Zabra sedang membaiki sistem…'}
        {on && primary === 'solutions' && '✨ Zabra ada solusinya — sebab itu kami "Solutions"!'}
        {on && primary === 'solved' && '✅ Selesai! Inilah yang Zabra Solutions lakukan untuk anda.'}
      </p>
    </div>
  );
};

export default VideoWall;
